import * as THREE from 'three';
import { HOTSPOT_CONFIG } from '../utils/Constants.js';
import { HotspotPlacementError } from '../utils/Errors.js';

/**
 * Manages custom image hotspots in the 360° scene
 */
export class HotspotManager {
    constructor(scene, camera, textureLoader, raycastHelper, sphereManager = null) {
        this.scene = scene;
        this.camera = camera;
        this.textureLoader = textureLoader;
        this.raycastHelper = raycastHelper;
        this.sphereManager = sphereManager;

        this.hotspots = [];
        this.isPlacing = false;
        this.currentTexture = null;
        this.currentSize = HOTSPOT_CONFIG.DEFAULT_SIZE;
        this.escapeHandler = null;

        // UI components (will be injected)
        this.crosshairOverlay = null;
        this.placementControls = null;

        // Callbacks
        this.onHotspotPlaced = null;
        this.onPlacementCancelled = null;
    }

    /**
     * Set UI components for crosshair and placement controls
     * @param {CrosshairOverlay} crosshairOverlay - Crosshair overlay component
     * @param {PlacementControls} placementControls - Placement controls component
     */
    setUIComponents(crosshairOverlay, placementControls) {
        this.crosshairOverlay = crosshairOverlay;
        this.placementControls = placementControls;
    }

    /**
     * Prepare a hotspot for placement
     * @param {File} file - Image file for the hotspot
     * @param {string} size - Size category (small, medium, large, original)
     */
    async prepareHotspot(file, size = HOTSPOT_CONFIG.DEFAULT_SIZE) {
        const url = URL.createObjectURL(file);
        const texture = await this.textureLoader.load360Texture(url);
        
        this.currentTexture = texture;
        this.currentSize = size;
        
        console.log(`✅ Hotspot prepared: ${file.name} (${size})`);
        return { texture, size };
    }

    /**
     * Start hotspot placement mode with crosshair
     * @param {Object} raycastTargets - Objects to raycast against
     * @param {Function} onCursorChange - Callback to change cursor
     * @param {string} helpText - Optional custom help text for placement
     */
    startPlacement(raycastTargets, onCursorChange, helpText = null) {
        if (!this.currentTexture) {
            console.warn('No hotspot texture prepared');
            return;
        }

        this.isPlacing = true;

        // Change cursor
        if (onCursorChange) {
            onCursorChange('grab'); // Keep normal cursor since we're using crosshair
        }

        // Show crosshair
        if (this.crosshairOverlay) {
            this.crosshairOverlay.show();
        }

        // Show placement controls with custom help text
        if (this.placementControls) {
            this.placementControls.show(
                () => this.confirmPlacement(raycastTargets, onCursorChange),
                () => this.cancelPlacement(onCursorChange),
                helpText
            );
        }

        // Setup escape handler
        this.escapeHandler = (event) => {
            if (event.key === 'Escape') {
                this.cancelPlacement(onCursorChange);
            }
        };
        document.addEventListener('keydown', this.escapeHandler);

        console.log('🎯 Hotspot placement mode started with crosshair');
    }

    /**
     * Confirm placement at crosshair position (center of screen)
     */
    confirmPlacement(raycastTargets, onCursorChange) {
        if (!this.isPlacing || !this.currentTexture) return;
        
        // Create a synthetic mouse event at the center of the screen
        const centerEvent = {
            clientX: window.innerWidth / 2,
            clientY: window.innerHeight / 2
        };
        
        // Perform raycast at center of screen
        const intersection = this.raycastHelper.raycast(centerEvent, raycastTargets);
        
        if (intersection) {
            this.placeHotspot(intersection.point, this.getCurrentImageIndex());
            this.finishPlacement(onCursorChange);
            
            if (this.onHotspotPlaced) {
                this.onHotspotPlaced();
            }
        } else {
            console.warn('[HotspotManager] No intersection found at center of screen');
            this.handlePlacementError('No valid surface found at crosshair position');
            // Don't finish placement, let user retry
        }
    }

    /**
     * Handle placement error
     * @param {string} message - Error message
     */
    handlePlacementError(message) {
        console.error(`[HotspotManager] Placement failed: ${message}`);

        // Notify via callback if available
        if (this.onPlacementFailed) {
            this.onPlacementFailed(message);
        }

        // Note: We don't finish placement here to allow the user to retry
    }

    /**
     * Place hotspot at a specific point
     * @param {THREE.Vector3} point - World position
     * @param {number} imageIndex - Associated image index
     */
    placeHotspot(point, imageIndex) {
        if (!this.currentTexture) return;
        
        // Calculate size
        const sizeMultiplier = HOTSPOT_CONFIG.SIZES[this.currentSize] || 1.0;
        const size = HOTSPOT_CONFIG.BASE_SIZE * sizeMultiplier;
        
        // Create geometry
        const geometry = new THREE.PlaneGeometry(size, size);
        
        // Create clean texture for hotspot
        const cleanTexture = this.textureLoader.createFlatTexture(this.currentTexture);
        
        // Create material
        const material = new THREE.MeshBasicMaterial({
            map: cleanTexture,
            transparent: true,
            opacity: 1.0,
            side: THREE.DoubleSide
        });
        
        // Create mesh
        const hotspot = new THREE.Mesh(geometry, material);
        
        // Position slightly inside the sphere surface
        const direction = point.clone().normalize();
        const offset = HOTSPOT_CONFIG.OFFSET_FROM_SURFACE;
        const worldPosition = point.clone().add(direction.multiplyScalar(offset));

        // Add to scene (not sphere) to avoid rotation conflicts between images
        hotspot.position.copy(worldPosition);
        hotspot.lookAt(this.camera.position);
        this.scene.add(hotspot);
        
        // Store hotspot data
        const hotspotData = {
            id: `hotspot_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            mesh: hotspot,
            imageIndex: imageIndex,
            imageId: this.currentImageId, // Store image ID if available
            imageName: this.currentImageName || null, // Store filename if available
            position: hotspot.position.clone(),
            size: this.currentSize,
            texture: cleanTexture,
            yaw: this.calculateYaw(point),
            pitch: this.calculatePitch(point)
        };

        this.hotspots.push(hotspotData);

        console.log(`✅ Hotspot placed at (${point.x.toFixed(2)}, ${point.y.toFixed(2)}, ${point.z.toFixed(2)})`);
        console.log(`📍 Yaw: ${hotspotData.yaw.toFixed(2)}°, Pitch: ${hotspotData.pitch.toFixed(2)}°`);
    }

    /**
     * Get current image index (placeholder - should be injected from App)
     * @returns {number}
     */
    getCurrentImageIndex() {
        // This will be set by the App when calling setUIComponents
        return this.currentImageIndex || 0;
    }

    /**
     * Set current image index
     * @param {number} index - Current image index
     */
    setCurrentImageIndex(index) {
        this.currentImageIndex = index;
    }

    /**
     * Finish placement mode
     */
    finishPlacement(onCursorChange) {
        this.cleanup();
        
        // Hide UI components
        if (this.crosshairOverlay) {
            this.crosshairOverlay.hide();
        }
        
        if (this.placementControls) {
            this.placementControls.hide();
        }
        
        if (onCursorChange) {
            onCursorChange('grab');
        }
    }

    /**
     * Cancel placement mode
     */
    cancelPlacement(onCursorChange) {
        this.cleanup();
        
        // Hide UI components
        if (this.crosshairOverlay) {
            this.crosshairOverlay.hide();
        }
        
        if (this.placementControls) {
            this.placementControls.hide();
        }
        
        if (onCursorChange) {
            onCursorChange('grab');
        }
        
        if (this.onPlacementCancelled) {
            this.onPlacementCancelled();
        }
    }

    /**
     * Cleanup placement event listeners
     */
    cleanup() {
        this.isPlacing = false;
        
        if (this.escapeHandler) {
            document.removeEventListener('keydown', this.escapeHandler);
            this.escapeHandler = null;
        }
    }

    /**
     * Show hotspots for a specific image (and hide all others)
     * @param {number} imageIndex - Image index
     */
    showForImage(imageIndex) {
        this.hotspots.forEach(hotspot => {
            // Show only hotspots for the current image, hide all others
            hotspot.mesh.visible = (hotspot.imageIndex === imageIndex);
        });
    }

    /**
     * Hide all hotspots (typically called during transitions)
     * @param {number} imageIndex - Image index (optional, for backwards compatibility)
     */
    hideForImage(imageIndex) {
        // Hide ALL hotspots during transition, regardless of image
        this.hotspots.forEach(hotspot => {
            hotspot.mesh.visible = false;
        });
    }

    /**
     * Remove all hotspots for a specific image
     * @param {number} imageIndex - Image index
     */
    removeForImage(imageIndex) {
        const toRemove = this.hotspots.filter(h => h.imageIndex === imageIndex);

        toRemove.forEach(hotspot => {
            // Remove from parent (sphere or scene)
            if (hotspot.mesh.parent) {
                hotspot.mesh.parent.remove(hotspot.mesh);
            }
            hotspot.mesh.geometry.dispose();
            hotspot.mesh.material.dispose();
        });

        this.hotspots = this.hotspots.filter(h => h.imageIndex !== imageIndex);
    }

    /**
     * Check if in placement mode
     * @returns {boolean}
     */
    isInPlacementMode() {
        return this.isPlacing;
    }

    /**
     * Calculate yaw from 3D position
     * @param {THREE.Vector3} point - World position
     * @returns {number} Yaw in degrees
     */
    calculateYaw(point) {
        // Use same formula as NavigationManager for consistency
        const normalized = point.clone().normalize();
        const yaw = Math.atan2(normalized.x, -normalized.z);
        return yaw * (180 / Math.PI);
    }

    /**
     * Calculate pitch from 3D position
     * @param {THREE.Vector3} point - World position
     * @returns {number} Pitch in degrees
     */
    calculatePitch(point) {
        // Use same formula as NavigationManager for consistency
        const normalized = point.clone().normalize();
        const pitch = Math.asin(normalized.y);
        return pitch * (180 / Math.PI);
    }

    /**
     * Remove a specific hotspot by ID
     * @param {string} hotspotId - The ID of the hotspot to remove
     */
    removeHotspot(hotspotId) {
        const hotspot = this.hotspots.find(h => h.id === hotspotId);
        if (!hotspot) {
            console.warn(`Hotspot ${hotspotId} not found`);
            return;
        }

        // Remove from parent (sphere or scene)
        if (hotspot.mesh.parent) {
            hotspot.mesh.parent.remove(hotspot.mesh);
        }

        // Dispose geometry and material
        hotspot.mesh.geometry.dispose();
        hotspot.mesh.material.dispose();

        // Remove from array
        this.hotspots = this.hotspots.filter(h => h.id !== hotspotId);

        console.log(`✅ Hotspot ${hotspotId} removed`);
    }

    /**
     * Set current image ID (for storing with hotspot)
     * @param {string} imageId - Current image ID
     */
    setCurrentImageId(imageId) {
        this.currentImageId = imageId;
    }

    /**
     * Convert spherical coordinates (yaw, pitch) to Cartesian (x, y, z)
     * @param {number} yaw - Yaw angle in degrees
     * @param {number} pitch - Pitch angle in degrees
     * @param {number} radius - Radius from center
     * @returns {THREE.Vector3} Cartesian position
     */
    sphericalToCartesian(yaw, pitch, radius) {
        // Use same formula as NavigationManager.pitchYawToPosition for consistency
        const latitude = pitch * (Math.PI / 180);
        const longitude = yaw * (Math.PI / 180);

        // Convert spherical coordinates to Cartesian (Three.js format)
        const x = radius * Math.cos(latitude) * Math.sin(longitude);
        const y = radius * Math.sin(latitude);
        const z = -radius * Math.cos(latitude) * Math.cos(longitude);

        return new THREE.Vector3(x, y, z);
    }

    /**
     * Create a hotspot mesh from texture and position
     * @param {THREE.Texture} texture - Texture for the hotspot
     * @param {THREE.Vector3} position - Position in 3D space
     * @param {string} size - Size category (small, medium, large)
     * @returns {THREE.Mesh} Created hotspot mesh
     */
    createHotspotMesh(texture, position, size) {
        // Calculate size
        const sizeMultiplier = HOTSPOT_CONFIG.SIZES[size] || 1.0;
        const meshSize = HOTSPOT_CONFIG.BASE_SIZE * sizeMultiplier;

        // Create geometry
        const geometry = new THREE.PlaneGeometry(meshSize, meshSize);

        // Create clean texture for hotspot
        const cleanTexture = this.textureLoader.createFlatTexture(texture);

        // Create material
        const material = new THREE.MeshBasicMaterial({
            map: cleanTexture,
            transparent: true,
            opacity: 1.0,
            side: THREE.DoubleSide
        });

        // Create mesh
        const mesh = new THREE.Mesh(geometry, material);

        // Set position
        mesh.position.copy(position);

        // Make the hotspot face the camera (look at center)
        mesh.lookAt(0, 0, 0);

        return mesh;
    }

    /**
     * Load hotspots from API data
     * @param {Array} customImages - Array of custom images from API
     * @param {Object} imageManager - Image manager to get image data
     */
    async loadHotspotsFromAPI(customImages, imageManager) {
        if (!Array.isArray(customImages) || customImages.length === 0) {
            console.log('[HotspotManager] No custom images to load from API');
            return;
        }

        console.log(`[HotspotManager] Loading ${customImages.length} hotspots from API...`);
        console.log(customImages);

        for (const customImage of customImages) {
            try {
                // Load texture from URL
                const texture = await this.textureLoader.loadStandardTexture(customImage.url);

                // Find the image this hotspot belongs to
                const imageIndex = imageManager.images.findIndex(img => img.id === customImage.imageId);
                if (imageIndex === -1) {
                    console.warn(`[HotspotManager] Image ${customImage.imageId} not found for hotspot ${customImage.id}`);
                    continue;
                }

                // Get the image data to access initialRotation
                const imageData = imageManager.images[imageIndex];
                const initialRotation = imageData.initialRotation || 0;

                // The yaw from placement is already compensated for sphere rotation
                // We DON'T need to adjust it further, as it's stored as an absolute position
                const adjustedYaw = customImage.position.yaw;

                console.log(`[HotspotManager] Hotspot ${customImage.id}:`);
                console.log(`  - Original yaw: ${customImage.position.yaw}°`);
                console.log(`  - Image initial rotation: ${initialRotation}°`);
                console.log(`  - Adjusted yaw: ${adjustedYaw}°`);

                // Create hotspot object
                const hotspot = {
                    id: customImage.id,
                    imageId: customImage.imageId,
                    imageIndex: imageIndex,
                    texture: texture,
                    size: customImage.size || 'medium',
                    yaw: adjustedYaw,
                    pitch: customImage.position.pitch,
                    imageName: customImage.name || null,
                    mesh: null
                };

                // Calculate position on sphere from adjusted yaw/pitch
                const radius = this.sphereManager ?
                    this.sphereManager.sphere.geometry.parameters.radius : 500;
                const position = this.sphericalToCartesian(
                    hotspot.yaw,
                    hotspot.pitch,
                    radius + HOTSPOT_CONFIG.OFFSET_FROM_SURFACE
                );

                // Create the hotspot mesh
                hotspot.mesh = this.createHotspotMesh(
                    texture,
                    position,
                    hotspot.size
                );

                // Hide it initially
                hotspot.mesh.visible = false;

                // Add to scene and array
                this.scene.add(hotspot.mesh);
                this.hotspots.push(hotspot);

                console.log(`[HotspotManager] ✅ Loaded hotspot: ${customImage.name || customImage.id}`);
                console.log(`  - Image index: ${imageIndex}, Image ID: ${customImage.imageId}`);
                console.log(`  - Position: yaw=${hotspot.yaw}°, pitch=${hotspot.pitch}°`);
                console.log(`  - 3D Position:`, hotspot.mesh.position);
                console.log(`  - Visible: ${hotspot.mesh.visible}`);

            } catch (error) {
                console.error(`[HotspotManager] Failed to load hotspot ${customImage.id}:`, error);
            }
        }

        console.log(`[HotspotManager] Loaded ${this.hotspots.length} hotspots successfully`);
    }

    /**
     * Cleanup and dispose all resources
     */
    dispose() {
        console.log('[HotspotManager] Disposing...');

        // Remove all hotspots from scene and dispose resources
        this.hotspots.forEach(hotspot => {
            if (hotspot.mesh && hotspot.mesh.parent) {
                hotspot.mesh.parent.remove(hotspot.mesh);
            }
            if (hotspot.mesh) {
                if (hotspot.mesh.geometry) {
                    hotspot.mesh.geometry.dispose();
                }
                if (hotspot.mesh.material) {
                    if (hotspot.mesh.material.map) {
                        hotspot.mesh.material.map.dispose();
                    }
                    hotspot.mesh.material.dispose();
                }
            }
        });

        this.hotspots = [];

        // Dispose placement preview mesh if exists
        if (this.previewMesh) {
            if (this.previewMesh.parent) {
                this.previewMesh.parent.remove(this.previewMesh);
            }
            if (this.previewMesh.geometry) {
                this.previewMesh.geometry.dispose();
            }
            if (this.previewMesh.material) {
                if (this.previewMesh.material.map) {
                    this.previewMesh.material.map.dispose();
                }
                this.previewMesh.material.dispose();
            }
            this.previewMesh = null;
        }

        // Clear callbacks
        this.onHotspotPlaced = null;
        this.onPlacementFailed = null;

        console.log('[HotspotManager] Disposed successfully');
    }
}

